import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import debounce from 'debounce';
import MarkerClusterer from '@google/markerclustererplus';
import PopulationCircle from './PopulationCircle';
import {
  setCoordinate,
  setElevationByCoordinate,
} from '../../store/actions';
import {
  setBounds,
  setProgress,
  setSelectedArea,
  setZoom,
} from '../../store/map';
import { setMapUi } from '../../store/ui';
import { getAllAltitudeValues } from '../../store/selectors';
import initilizeMap, {
  getGeocoder,
  getMap,
  getMapLocations,
  getRasterManager,
} from '../../lib/mapService';
import setPanoramaById from '../../store/actions/setPanoramaById';
import { setRotation } from '../../store/panorama';
import setElevation from '../../store/actions/setElevation';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const MapElement = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;

  .cluster div {
    outline: none;
  }
`;

const MapControls = styled.div`
  position: absolute;
  right: 8px;
  bottom: 85px;
  width: 42px;
  pointer-events: none;
`;

const ZoomButton = styled.button`
  font-family: 'Source Code Pro',monospace;
  font-size: 20px;
  line-height: 15px;
  font-weight: 1000;
  margin: 0;
  padding: 12px;
  background-color: transparent;
  text-align: center;
  color: white;
  cursor: pointer;
  border: 2px solid rgba(255, 255, 255, 0.6);
  text-shadow: 0 0 0.4em black;
  box-shadow: 0 0 0.6em rgba(0,0,0,0.2), inset 0 0 0.6em rgba(0,0,0,0.2);
  background-color: rgba(0,0,0,0.4);
  pointer-events: auto;

  &:hover {
    background-color: rgba(0,0,0,0.8);
  }

  &:first-child {
    margin-bottom: 20px;
  }
`;

const MOVE_DEBOUNCE_TIME = 500;

/**
 * Map view of the coordinate.
 */
class MapUi extends React.Component {
  static propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    altitudes: PropTypes.array.isRequired,
    altitude: PropTypes.number.isRequired,
    coordinate: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }).isRequired,
    isMapUi: PropTypes.bool.isRequired,
    showPanorama: PropTypes.func.isRequired,
    updateBounds: PropTypes.func.isRequired,
    updatePosition: PropTypes.func.isRequired,
    updateProgress: PropTypes.func.isRequired,
    updateZoom: PropTypes.func.isRequired,
    zoom: PropTypes.number.isRequired,
  };

  /**
   * @param {object} props Properties of the MapUi.
   */
  constructor(props) {
    super(props);

    this.state = { map: null };

    this.geocoder = null;
    this.populationArea = null;
    this.mapInteractive = false;

    this.mapRef = React.createRef();

    this.zoomOut = this.zoomOut.bind(this);
    this.zoomIn = this.zoomIn.bind(this);
    this.setBounds = debounce(this.setBounds.bind(this), MOVE_DEBOUNCE_TIME);
    this.updateAltitudeLayer = debounce(
        this.updateAltitudeLayer.bind(this),
        100,
    );
  }

  /**
   * React lifecycle handler.
   */
  async componentDidMount() {
    const {
      altitude,
      altitudes,
      coordinate,
      updateProgress,
      zoom,
    } = this.props;

    initilizeMap({
      altitudes,
      altitude,
      updateProgress,
      cameraDirection: { lat: 0, lng: 0 },
      mapElement: this.mapRef.current,
      mapOptions: {
        center: coordinate,
        zoom,
      },
    });

    const map = getMap();

    this.geocoder = getGeocoder();

    getMapLocations().then((locations) => {
      const { google } = window;
      const anchor = new google.maps.Point(20, 20);
      const scaledSize = new google.maps.Size(40, 40);

      const markers = locations.map((location) => {
        const {
          lat,
          lng,
          panoramaId,
          x,
          y,
          z,
          elevation,
        } = location;

        const marker = new google.maps.Marker({
          position: new google.maps.LatLng(lat, lng),
          icon: {
            anchor,
            scaledSize,
            url: `${process.env.PUBLIC_URL}/map_marker.png`,
          },
        });

        marker.addListener('click', () => {
          const { showPanorama } = this.props;
          showPanorama(
              panoramaId,
              { lat, lng },
              x !== undefined && y !== undefined && z !== undefined
                ? { x, y, z }
                : null,
              elevation !== undefined ? elevation : null,
          );
        });

        return marker;
      });

      return new MarkerClusterer(map, markers, {
        enableRetinaIcons: true,
        maxZoom: 12,
        styles: [MarkerClusterer.withDefaultStyle({
          url: `${process.env.PUBLIC_URL}/map_cluster.png`,
          width: 80,
          height: 80,
          textSize: 16,
          fontFamily: 'Source Sans Pro',
          textColor: 'white',
        })],
      });
    });

    map.addListener('bounds_changed', () => {
      this.mapInteractive = true;
      this.setBounds();
    });

    map.addListener('idle', debounce(() => {
      this.mapInteractive = false;
    }, MOVE_DEBOUNCE_TIME));

    this.setState({ map });
  }

  /**
   * React lifecycle handler.
   * @param {object} nextProps New props of the instance.
   * @param {object} nextState New state of the instance.
   * @return {boolean} Should component update.
   */
  shouldComponentUpdate(nextProps, nextState) {
    const {
      altitude,
      coordinate,
      isMapUi,
    } = this.props;
    const { map } = this.state;

    if (!map && nextState.map) {
      return true;
    }

    // from streetview to map
    if (!isMapUi && nextProps.isMapUi) {
      return true;
    }

    // from map to streetview
    if (isMapUi && !nextProps.isMapUi) {
      return true;
    }

    if (nextProps.isMapUi) {
      if (nextProps.altitude !== altitude) {
        this.updateAltitudeLayer(nextProps.altitude);
      }

      if (map && !this.mapInteractive && nextProps.coordinate !== coordinate) {
        setTimeout(() => map.setCenter(nextProps.coordinate), 0);
      }

      if (map && !this.mapInteractive && map.getZoom() !== nextProps.zoom) {
        setTimeout(() => map.setZoom(nextProps.zoom), 0);
      }
    }

    // Do not rerender - rerenders the map.
    return false;
  }

  /**
   * React lifecycle method
   * @param {object} prevProps The previous props
   * @param {object} prevState The previous state
   */
  componentDidUpdate(prevProps, prevState) {
    const {
      isMapUi,
      coordinate,
      altitude,
      zoom,
    } = this.props;

    const { map } = this.state;

    if (isMapUi) {
      if (!prevProps.isMapUi) {
        map.setCenter(coordinate);
        map.setZoom(zoom);
        this.updateAltitudeLayer(altitude);
      }
    } else if (prevProps.isMapUi) {
      this.updateAltitudeLayer(altitude);
    }
  }

  /**
   * React lifecycle handler.
   */
  componentWillUnmount() {
    const { google } = window;
    google.maps.event.clearListeners(this.map, 'bounds_changed');
  }

  /**
   * Update bounds to store.
   */
  setBounds() {
    const {
      updateBounds,
      updatePosition,
      coordinate,
      zoom,
      isMapUi,
    } = this.props;

    const { map } = this.state;
    const bounds = map && map.getBounds();

    if (bounds) {
      updateBounds(bounds.toJSON());

      if (isMapUi) {
        const mapCenter = map.getCenter().toJSON();
        const mapZoom = map.getZoom();

        if (mapCenter.lat !== coordinate.lat
          || mapCenter.lng !== coordinate.lng
          || mapZoom !== zoom) {
          updatePosition(mapCenter, mapZoom);
        }
      }
    }
  }

  /**
   * Update zoom from map.
   * @param {MapEvent} event from the map
   */
  setZoomFromMap() {
    const { updateZoom, zoom } = this.props;
    const { map } = this.state;
    const mapZoom = map.getZoom();

    if (zoom !== mapZoom) {
      updateZoom(mapZoom);
    }
  }

  /**
   * Handles updating of the elevation layer.
   * @param {!number} altitude Altitude for drawing elevation layer.
   */
  updateAltitudeLayer(altitude) { // eslint-disable-line class-methods-use-this
    const rasterManager = getRasterManager();

    if (rasterManager) {
      rasterManager.setClip(altitude);
      rasterManager.update();
    }
  }

  /**
   * Zoom map in.
   */
  zoomIn() {
    const { updateZoom, zoom } = this.props;
    updateZoom(zoom + 1);
  }

  /**
   * Zoom map out.
   */
  zoomOut() {
    const { updateZoom, zoom } = this.props;
    updateZoom(zoom - 1);
  }

  /**
   * React lifecycle handler.
   * @return {jsx}
   */
  render() {
    const { isMapUi } = this.props;
    const { map } = this.state;

    return (
      <Wrapper className={isMapUi ? '' : 'hidden'}>
        <PopulationCircle map={map} />

        <MapElement ref={this.mapRef} />

        <MapControls>
          <ZoomButton
            id="zoom-in"
            aria-label="zoom in"
            onClick={this.zoomIn}
          >
            +
          </ZoomButton>

          <ZoomButton
            id="zoom-out"
            aria-label="zoom out"
            onClick={this.zoomOut}
          >
            -
          </ZoomButton>
        </MapControls>
      </Wrapper>
    );
  }
}

export default connect(
    (state) => {
      const { map, ui, altitudes } = state;

      return {
        altitude: altitudes.value,
        altitudes: getAllAltitudeValues(state),
        coordinate: map.coordinate,
        isMapUi: ui.isMapUi,
        zoom: map.zoom,
      };
    },

    (dispatch) => ({
      updateBounds: (bounds) => dispatch(setBounds(bounds)),
      updatePosition: (coords, zoom) => {
        dispatch(setZoom(zoom, false));
        dispatch(setCoordinate(coords));
      },
      updateProgress: (progress) => dispatch(setProgress(progress)),
      updateZoom: (zoom) => dispatch(setZoom(zoom)),
      showPanorama: (
          panoramaId,
          coordinate,
          cameraRotation,
          elevation,
      ) => {
        dispatch(setMapUi(false, false));
        dispatch(setCoordinate(coordinate, false));

        if (elevation !== null) {
          dispatch(setElevation(elevation));
        } else {
          dispatch(setElevationByCoordinate(coordinate));
        }

        dispatch(setSelectedArea({
          radius: 5000,
          ...coordinate,
        }, false));

        dispatch(setRotation(cameraRotation, false));
        dispatch(setPanoramaById(panoramaId, cameraRotation));
      },
    }),
)(MapUi);
